import { WrapperEntitySelector } from '@components/Inputs';
import Duration from '@components/Inputs/Duration';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { Grid, RadioButtonGroup, Select, Tooltip, Typography } from '@ts/storybook';
import { CEGRNoChangeStatus, IForm, TMode } from '@ts/ui-models';
import ThresholdSection from './ThresholdSection';
import { IconTooltip } from '@components/Outputs';

interface IEGRNoStatusChangeProps extends IForm {
  mode: TMode;
  values: CEGRNoChangeStatus;
  text: string;
  withStatusIndicator: boolean;
}

const EGRNoStatusChange = (props: IEGRNoStatusChangeProps) => {
  const { mode, values: EGR, text, withStatusIndicator, onCallbackChange } = props;
  const isViewMode = mode === 'view';

  //#region Handlers
  const onChangeEntitySelector = entityKey => entity => {
    const fkValue = entity ? entity.getID() : null;
    onCallbackChange(entityKey + 'Id')(fkValue);
    onCallbackChange(entityKey)(entity);
  };

  const onChangeTimeConfig = entityName => entity => {
    const copyTimeOut = { ...EGR.timeoutConfig };
    copyTimeOut[entityName] = entity;
    onCallbackChange('timeoutConfig')(copyTimeOut);
  };
  //#endregion

  const renderValueStatusIndicators = indicatorType => {
    if (!indicatorType) return;

    const optionsPossibleValues = indicatorType?.possibleValues?.map(v => {
      return { label: v, value: v };
    });

    switch (indicatorType.type) {
      case 'list':
        return (
          <>
            <Grid>
              <Typography variant="body1" className="p-4.5" text="with" />
            </Grid>
            <Grid size={{ xs: 8, md: 3 }}>
              <Select
                isReadOnly={isViewMode}
                options={optionsPossibleValues}
                label="Value"
                value={EGR.actionConfig?.statusIndicatorValue}
                onChange={v => {
                  onCallbackChange('actionConfig')({
                    statusIndicatorValue: v,
                  });
                }}
              />
            </Grid>
            <Grid>
              <Typography variant="body1" className="p-4.5" text="System record event" />
            </Grid>
          </>
        );

      default:
        return (
          <Grid>
            <Typography variant="body1" className="p-4.5" text="System record event" />
          </Grid>
        );
    }
  };

  return (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid container spacing={1}>
        <Typography variant="body1" className="p-2" text="After" />
        <IconTooltip text="TrackSphere monitor" />
      </Grid>
      <Grid>
        <Duration
          value={EGR.timeoutConfig?.deadlineTriggerDelay || 0}
          onChange={onChangeTimeConfig('deadlineTriggerDelay')}
          isReadOnly={isViewMode}
        />
      </Grid>
      <Grid>
        <Typography variant="body1" className="p-2" text={text} />
      </Grid>
      {withStatusIndicator && (
        <Grid container size={{ xs: 12 }}>
          <Grid size={{ xs: 8, md: 4 }}>
            <WrapperEntitySelector
              label="Status indicators"
              searchComponent={generateSearchEntitySelector('indicatorType')}
              value={EGR.indicatorType ?? EGR.indicatorTypeId}
              onChangeEntity={onChangeEntitySelector('indicatorType')}
              isReadOnly={isViewMode}
            />
          </Grid>
          {renderValueStatusIndicators(EGR.indicatorType)}
        </Grid>
      )}
      {/* {withSelector && (
        <Grid size={{ xs: 12 }}>
          <WrapperEntitySelector
            searchComponent={generateSearchEntitySelector('eventType')}
            value={values.noEventRecordedType ?? timeoutConfig.noEventRecordedTypeId}
            onChangeEntity={onChangeNoEventRecorded('noEventRecordedType')}
            label="Specific event type"
            isReadOnly={isViewMode}
          />
        </Grid>
      )} */}
      <Grid size={{ xs: 12 }}>
        <RadioButtonGroup
          value={EGR.actionConfig?.noChangeStatusTriggerField ?? 'lastChange'}
          onChange={onCallbackChange(`actionConfig.noChangeStatusTriggerField`)}
          disabled={isViewMode}
          options={[
            {
              value: 'lastChange',
              label: [<b>Last Change Date</b>, ' (', 'The last time the status change to a different value', ')'],
            },
            {
              value: 'lastUpdate',
              label: [
                <b>Last Update Date</b>,
                ' (',
                'The most recent time the status was updated, even if the value remained the same',
                ')',
              ],
            },
          ]}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <ThresholdSection {...props} EGR={new CEGRNoChangeStatus(EGR)} />
      </Grid>
    </Grid>
  );
};

export default EGRNoStatusChange;
